import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const SUPPORTED_LANGUAGES = [
  { code: 'nl', name: 'Nederlands', flag: 'NL' },
  { code: 'en', name: 'English', flag: 'GB' },
  { code: 'de', name: 'Deutsch', flag: 'DE' },
];

/**
 * Detect the best language from browser navigator.languages / navigator.language.
 * Priority: nl → en → de → nl (Hollanda pazarı default)
 */
function detectBrowserLanguage() {
  const languages = navigator.languages || [navigator.language || 'nl'];
  for (const lang of languages) {
    const code = lang.toLowerCase().split('-')[0];
    if (code === 'nl') return 'nl';
    if (code === 'en') return 'en';
    if (code === 'de') return 'de';
  }
  return 'nl'; // Hollanda pazarı — varsayılan NL
}

/**
 * Determine initial language with priority:
 * 1. ?lang= URL query param (for direct sharing)
 * 2. User's saved preference (localStorage) — respect explicit choice
 * 3. Fallback: 'nl' — always default to Dutch for SEO consistency.
 *    Browser language detection removed: crawlers use Accept-Language:en
 *    which caused React to render in English, mismatching hreflang="nl".
 *    Users who want EN/DE can use the language toggle explicitly.
 */
function getInitialLanguage() {
  // Priority 1: URL query param ?lang=nl|en|de
  const urlParams = new URLSearchParams(window.location.search);
  const queryLang = urlParams.get('lang');
  if (queryLang && ['nl', 'en', 'de'].includes(queryLang)) {
    localStorage.setItem('user-selected-language', queryLang);
    return queryLang;
  }

  // Priority 2: Previously saved user preference (explicit choice only)
  const saved = localStorage.getItem('user-selected-language');
  if (saved && ['nl', 'en', 'de'].includes(saved)) {
    return saved;
  }

  // Priority 3: Default to Dutch
  return 'nl';
}

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(getInitialLanguage);

  // Note: document.documentElement.lang is NOT mutated here.
  // <html lang="nl"> is set statically in public/index.html and kept stable
  // for SEO correctness (hreflang="nl" matches html lang="nl").
  // Dynamic lang change caused Ahrefs to flag "hreflang/html lang mismatch" on 87 pages.

  const handleSetLanguage = (lang) => {
    if (!['nl', 'en', 'de'].includes(lang)) return;
    setLanguage(lang);
    // Mark as explicit user choice — will be respected on next visit
    localStorage.setItem('user-selected-language', lang);
  };

  const t = (translations) => {
    return translations[language] || translations['nl'];
  };

  return (
    <LanguageContext.Provider value={{
      language,
      setLanguage: handleSetLanguage,
      t,
      languages: SUPPORTED_LANGUAGES
    }}>
      {children}
    </LanguageContext.Provider>
  );
};
